/**
 * Wire shape + parser for the local `MockPriceSocket` used by the LiveTrading
 * chart's fallback path. The production chart consumes canonical
 * `InstrumentTick`s from `useInstrumentRealtime` directly (see
 * `useLightweightChart`), but the mock keeps the original Plaee-shaped payload
 * so the chart's mock path stays faithful to the previous live wire shape.
 */

/**
 * Raw payload broadcast by the backend on `instrument:price`.
 *
 * Shape confirmed against `predictions-api` (`WsInstrumentUpdated`) and a real
 * live tick captured on 2026-05-12:
 * ```
 * {
 *   event: "prediction:market:event",
 *   type: "instrument_updated",
 *   instrumentCode: "BTC",
 *   symbol: "FS:XBTUSD:BLD",
 *   sendingTime: "2026-05-12T09:48:42.974Z",
 *   lastPrice: 80844.919,
 *   ...
 * }
 * ```
 */
export interface InstrumentTickPayload {
  event?: 'prediction:market:event' | string;
  type?: 'instrument_updated' | string;
  /** Asset code (`"BTC"`, `"ADA"`, ...) — the routing key. */
  instrumentCode?: string;
  /** Trading pair (`"FS:XBTUSD:BLD"`) — informational, not the routing key. */
  symbol?: string;
  /** ISO 8601 timestamp with millisecond precision. */
  sendingTime?: string;
  /** Spot price of the underlying asset. */
  lastPrice?: number;
  highPx?: number;
  lowPx?: number;
  openMidPx?: number;
  highMidPx?: number;
  lowMidPx?: number;
  lastMidPx?: number;
  aggressorSide?: number;
  [key: string]: unknown;
}

/** A normalized price tick consumed by the chart. */
export interface PriceTick {
  /** Underlying asset spot price. */
  price: number;
  /**
   * UTC timestamp in seconds (Unix epoch). Fractional — sub-second precision is
   * preserved from `sendingTime` so multiple ticks within the same second don't
   * collapse onto the same bar.
   */
  time: number;
}

/**
 * Convert a wire `InstrumentTickPayload` to a `PriceTick`, or return `null`
 * when the payload doesn't carry a usable price.
 */
export function parseInstrumentTick(raw: InstrumentTickPayload): PriceTick | null {
  const price = typeof raw.lastPrice === 'number' ? raw.lastPrice : null;
  if (price === null) return null;

  const time = coerceTimeSeconds(raw.sendingTime);
  return { price, time };
}

/**
 * Convert a flexible time value (seconds, milliseconds, or ISO string) into
 * fractional UTC seconds. Falls back to `Date.now()` when the value is
 * missing or unparseable.
 */
export function coerceTimeSeconds(value: string | number | undefined): number {
  if (typeof value === 'number') {
    return value > 1e12 ? value / 1000 : value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed / 1000;
  }
  return Date.now() / 1000;
}
